import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

// Types
export interface Business {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  ghlBaseUrl: string | null;
  ghlPitToken: string | null;
  ghlLocationId: string | null;
  isActive: boolean;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  memberRole?: string;
}

export interface Agent {
  id: string;
  businessId: string;
  name: string;
  slug: string;
  description: string | null;
  voiceProvider: 'openai' | 'elevenlabs' | 'google';
  voiceId: string | null;
  voiceSettings: {
    speed?: number;
    pitch?: number;
    stability?: number;
    similarityBoost?: number;
  } | null;
  systemPrompt: string;
  greetingMode: 'none' | 'predefined' | 'generated';
  startingMessage: string | null;
  calendarId: string | null;
  timezone: string | null;
  // SIP/Phone Configuration
  outboundPhoneNumber: string | null;
  sipTrunkHost: string | null;
  sipAuthUsername: string | null;
  sipAuthPassword: string | null;
  inboundPhoneNumber: string | null;
  inboundSipUri: string | null;
  llmProvider: 'openai' | 'anthropic';
  model: string | null;
  temperature: number | null;
  maxTokens: number | null;
  isActive: boolean;
  isDefault: boolean;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface VoiceInfo {
  id: string;
  name: string;
  language?: string;
  gender?: 'male' | 'female' | 'neutral';
  preview_url?: string;
  description?: string;
}

interface BusinessState {
  // Current selection
  currentBusinessId: string | null;
  currentAgentId: string | null;

  // Data
  businesses: Business[];
  agents: Agent[];
  voices: Record<string, VoiceInfo[]>; // keyed by provider

  // Loading states
  isLoadingBusinesses: boolean;
  isLoadingAgents: boolean;
  isLoadingVoices: boolean;

  // Error
  error: string | null;

  // Actions
  setCurrentBusiness: (id: string | null) => void;
  setCurrentAgent: (id: string | null) => void;

  // Business CRUD
  fetchBusinesses: () => Promise<void>;
  createBusiness: (data: {
    name: string;
    slug: string;
    ghlPitToken?: string;
    ghlLocationId?: string;
  }) => Promise<Business>;
  updateBusiness: (id: string, data: Partial<Business>) => Promise<Business>;
  deleteBusiness: (id: string) => Promise<void>;

  // Agent CRUD
  fetchAgents: (businessId: string) => Promise<void>;
  createAgent: (businessId: string, data: Partial<Agent>) => Promise<Agent>;
  updateAgent: (id: string, data: Partial<Agent>) => Promise<Agent>;
  deleteAgent: (id: string) => Promise<void>;

  // Voice
  fetchVoices: (provider: string, businessId?: string) => Promise<VoiceInfo[]>;

  // Helpers
  getCurrentBusiness: () => Business | null;
  getCurrentAgent: () => Agent | null;
  clearError: () => void;
}

export const useBusinessStore = create<BusinessState>()(
  persist(
    (set, get) => ({
      currentBusinessId: null,
      currentAgentId: null,
      businesses: [],
      agents: [],
      voices: {},
      isLoadingBusinesses: false,
      isLoadingAgents: false,
      isLoadingVoices: false,
      error: null,

      setCurrentBusiness: (id) => {
        set({ currentBusinessId: id, currentAgentId: null, agents: [] });
        if (id) {
          get().fetchAgents(id);
        }
      },

      setCurrentAgent: (id) => set({ currentAgentId: id }),

      fetchBusinesses: async () => {
        set({ isLoadingBusinesses: true, error: null });
        try {
          const response = await api.get<Business[]>('/businesses');
          const businesses = response.data || [];
          set({ businesses, isLoadingBusinesses: false });

          // Auto-select first business if none selected
          if (!get().currentBusinessId && businesses.length > 0) {
            get().setCurrentBusiness(businesses[0].id);
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch businesses',
            isLoadingBusinesses: false,
          });
        }
      },

      createBusiness: async (data) => {
        set({ error: null });
        try {
          const response = await api.post<Business>('/businesses', data);
          const business = response.data!;
          set((state) => ({ businesses: [...state.businesses, business] }));
          return business;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create business';
          set({ error: message });
          throw error;
        }
      },

      updateBusiness: async (id, data) => {
        set({ error: null });
        try {
          const response = await api.patch<Business>(`/businesses/${id}`, data);
          const business = response.data!;
          set((state) => ({
            businesses: state.businesses.map((b) => (b.id === id ? business : b)),
          }));
          return business;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update business';
          set({ error: message });
          throw error;
        }
      },

      deleteBusiness: async (id) => {
        set({ error: null });
        try {
          await api.delete(`/businesses/${id}`);
          set((state) => ({
            businesses: state.businesses.filter((b) => b.id !== id),
            currentBusinessId: state.currentBusinessId === id ? null : state.currentBusinessId,
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete business';
          set({ error: message });
          throw error;
        }
      },

      fetchAgents: async (businessId) => {
        set({ isLoadingAgents: true, error: null });
        try {
          const response = await api.get<Agent[]>(`/businesses/${businessId}/agents`);
          const agents = response.data || [];
          set({ agents, isLoadingAgents: false });

          // Auto-select default agent
          const defaultAgent = agents.find((a) => a.isDefault) || agents[0];
          if (defaultAgent && !get().currentAgentId) {
            set({ currentAgentId: defaultAgent.id });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch agents',
            isLoadingAgents: false,
          });
        }
      },

      createAgent: async (businessId, data) => {
        set({ error: null });
        try {
          const response = await api.post<Agent>(`/businesses/${businessId}/agents`, data);
          const agent = response.data!;
          set((state) => ({ agents: [...state.agents, agent] }));
          return agent;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create agent';
          set({ error: message });
          throw error;
        }
      },

      updateAgent: async (id, data) => {
        set({ error: null });
        try {
          const response = await api.patch<Agent>(`/agents/${id}`, data);
          const agent = response.data!;
          set((state) => ({
            agents: state.agents.map((a) => (a.id === id ? agent : a)),
          }));
          return agent;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update agent';
          set({ error: message });
          throw error;
        }
      },

      deleteAgent: async (id) => {
        set({ error: null });
        try {
          await api.delete(`/agents/${id}`);
          set((state) => ({
            agents: state.agents.filter((a) => a.id !== id),
            currentAgentId: state.currentAgentId === id ? null : state.currentAgentId,
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete agent';
          set({ error: message });
          throw error;
        }
      },

      fetchVoices: async (provider, businessId) => {
        set({ isLoadingVoices: true });
        try {
          const query = businessId ? `?businessId=${businessId}` : '';
          const response = await api.get<VoiceInfo[] | { voices: VoiceInfo[]; message?: string }>(`/voice/voices/${provider}${query}`);
          // Handle both array response and object with voices property
          let voices: VoiceInfo[] = [];
          if (Array.isArray(response.data)) {
            voices = response.data;
          } else if (response.data && Array.isArray((response.data as { voices: VoiceInfo[] }).voices)) {
            voices = (response.data as { voices: VoiceInfo[] }).voices;
          }
          set((state) => ({
            voices: { ...state.voices, [provider]: voices },
            isLoadingVoices: false,
          }));
          return voices;
        } catch (error) {
          set({ isLoadingVoices: false });
          return [];
        }
      },

      getCurrentBusiness: () => {
        const { currentBusinessId, businesses } = get();
        return businesses.find((b) => b.id === currentBusinessId) || null;
      },

      getCurrentAgent: () => {
        const { currentAgentId, agents } = get();
        return agents.find((a) => a.id === currentAgentId) || null;
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'mia-business',
      partialize: (state) => ({
        currentBusinessId: state.currentBusinessId,
        currentAgentId: state.currentAgentId,
      }),
    }
  )
);

